"use client";

import dynamic from "next/dynamic";
import React, { useEffect, useMemo, useState } from "react";

const Plot = dynamic(() => import("react-plotly.js"), { ssr: false });

type Resp = {
  statistics: Array<{
    data: any[];
    layout: any;
    legend?: Array<{ label: string; color: string }>;
  }>;
};

type Props = {
  /** When provided with `payload`, component will POST and render the result. */
  endpoint?: string;
  payload?: unknown;

  /** If you already have the response (e.g., from another chart), pass it here. */
  initial?: Resp;

  /** Which plot to draw from the response */
  render?: "area" | "emission";

  /** Style/helpers */
  className?: string;
  height?: number;

  /** Called only on successful fetch */
  onLoaded?: (resp: Resp) => void;
};

export default function EcotopeCharts({
  endpoint,
  payload,
  initial,
  render = "area",
  className,
  height = 360,
  onLoaded,
}: Props) {
  const [resp, setResp] = useState<Resp | null>(initial ?? null);
  const [loading, setLoading] = useState<boolean>(!initial && !!endpoint);
  const [error, setError] = useState<string | null>(null);

  // fetch only if we don't have initial data
  useEffect(() => {
    let abort = false;
    async function run() {
      if (initial) {
        setResp(initial);
        setLoading(false);
        setError(null);
        return;
      }
      if (!endpoint || !payload) {
        // don’t fetch; show an explicit message so you catch wiring mistakes
        setLoading(false);
        setError("Missing endpoint/payload");
        return;
      }
      try {
        setLoading(true);
        setError(null);

        const res = await fetch(endpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
          // credentials: "omit" // <= leave default unless your API needs cookies
        });

        if (!res.ok) {
          const t = await res.text().catch(() => "");
          throw new Error(`HTTP ${res.status} ${res.statusText} ${t}`.trim());
        }

        const json = (await res.json()) as Resp;
        if (abort) return;

        setResp(json);
        setLoading(false);
        setError(null);
        onLoaded?.(json);
      } catch (e: any) {
        if (abort) return;
        console.error("EcotopeCharts fetch failed:", e);
        setLoading(false);
        setError(e?.message || "Failed to load chart.");
      }
    }
    run();
    return () => {
      abort = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, JSON.stringify(payload), initial]);

  // pick the correct plot from the response
  const plot = useMemo(() => {
    if (!resp?.statistics?.length) return null;
    const idx = render === "area" ? 0 : 1;
    return resp.statistics[idx] ?? null;
  }, [resp, render]);

  if (loading) {
    return (
      <div
        className={className}
        style={{
          height,
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          display: "grid",
          placeItems: "center",
          background: "#fff",
          color: "#6b7280",
        }}
      >
        Loading chart…
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={className}
        style={{
          height,
          border: "1px solid #fecaca",
          background: "#fff1f2",
          color: "#991b1b",
          borderRadius: 12,
          display: "grid",
          placeItems: "center",
          padding: 16,
          textAlign: "center",
        }}
      >
        Couldn’t load chart: {error}
      </div>
    );
  }

  if (!plot) {
    return (
      <div
        className={className}
        style={{
          height,
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          display: "grid",
          placeItems: "center",
          background: "#fff",
          color: "#6b7280",
        }}
      >
        No data.
      </div>
    );
  }

  // Give Plotly a stable but changing key so it re-renders cleanly when the plot switches
  const plotKey = `${render}-${(plot.data?.length ?? 0)}-${(plot.layout?.title ?? "")}`;

  return (
    <div className={className} style={{ background: "#fff", borderRadius: 12 }}>
      <Plot
        key={plotKey}
        data={plot.data}
        layout={{
          ...plot.layout,
          autosize: true,
          height,
          margin: { l: 60, r: 30, t: 50, b: 60 },
          paper_bgcolor: "#ffffff",
          plot_bgcolor: "#ffffff",
        }}
        useResizeHandler
        style={{ width: "100%", height }}
        config={{
          responsive: true,
          displayModeBar: true,            // ✅ enable toolbar
          displaylogo: false,              // 🚫 disable "Plotly" logo
          modeBarButtonsToRemove: [
            "zoom2d",
            "pan2d",
            "select2d",
            "lasso2d",
            "zoomIn2d",
            "zoomOut2d",
            "autoScale2d",                // Optional: this is the actual home button, remove this line to keep it!
          ],
        }}
      />
    </div>
  );
}